import * as React from 'react';
import {
	Button,
	fromThemeColors,
	layout,
	styleUtils,
	ThemeColor,
	withModals,
	WithModalsProps,
} from '@citrite/web-ui-component';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { GenericErrorBoundary } from 'App/GenericErrorBoundary';
import { useFormFactor } from 'App/Header/useFormFactor';
import { useInScrolledView } from 'App/Screen/useInScrolledView';
import { FavoriteIconWithRef } from 'App/Tile/FavoriteIcon';
import { ResourceDetailsWithFavoriteRef } from 'App/Tile/ResourceDetails';
import { ResourceMenu } from 'App/Tile/ResourceMenu';
import { useBrowserExtensionPrompt } from 'App/Tile/useBrowserExtensionPrompt';
import { useResourceContextMenuBuilder } from 'App/Tile/useResourceContextMenuBuilder';
import { launchResourceOnClick, shouldShowTroubleshootOption } from 'App/Tile/utils';
import { performance, PerformanceEvents } from 'Components/performance';
import { useConfigurationContext } from 'Configuration/useConfigurationContext';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { LaunchStatus } from 'Environment/launchResource/launchResource';
import { useFeatureCanary } from 'utils/useFeatureCanary';
import { useLoadableResourceContext } from 'Workspace/ResourceProvider';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';
import { useUserContext } from 'Workspace/UserContext';

const largeFormButtonWrapperStyle = {
	width: '100%',
};

export const smallFormButtonWrapperStyle = {
	display: 'block',
};

export const StyledDiv = styled.div<{
	isSmallFormFactor: boolean;
	isResourceAvailable: boolean;
	isOffWhiteBackground: boolean;
	isDetailedDesktopViewEnabled?: boolean;
}>`
	position: relative;
	border: 1px solid ${fromThemeColors(ThemeColor.border)};
	min-width: 160px;
	height: 168px;
	box-sizing: border-box;
	background-color: ${fromThemeColors('primaryContainerBackground')};
	padding: ${layout.smallSpace};
	border-radius: ${layout.space12};
	box-shadow: ${props =>
		props.isOffWhiteBackground && !props.isSmallFormFactor
			? styleUtils.shadow2.boxShadow
			: 'none'};
	cursor: pointer;
	&:hover {
		transition: box-shadow 0.1s linear;
		box-shadow: ${styleUtils.shadow3.boxShadow};
	}
	${({ isSmallFormFactor }) =>
		isSmallFormFactor &&
		css`
			display: flex;
			height: 56px;
			padding: ${layout.smallSpace} 0;
			width: auto;
			flex-direction: row;
			border: none;
			&:hover {
				box-shadow: none;
			}
			justify-content: space-between;
		`}

	${({ isResourceAvailable, isSmallFormFactor }) =>
		!isResourceAvailable &&
		css`
			opacity: 0.5;
			cursor: not-allowed;
			box-shadow: ${!isSmallFormFactor ? styleUtils.shadow1.boxShadow : 'none'};
			&:hover {
				box-shadow: ${!isSmallFormFactor ? styleUtils.shadow1.boxShadow : 'none'};
			}
			* {
				pointer-events: none;
			}
		`}
`;
interface Props extends WithModalsProps {
	resource: Resource;
	isResourceAvailable: boolean;
}

export const DivWrapper = (props: { children: React.ReactNode }) => (
	<div {...props}>{props.children}</div>
);

function __GenericTile({ resource, isResourceAvailable, showModal }: Props) {
	const { value: resourceContext } = useLoadableResourceContext();
	const { workspaceConfiguration } = useConfigurationContext();
	const { userDetails } = useUserContext();
	const hasSubscriptionsEnabled = resourceContext.subscriptionsEnabled();
	const favoriteRef = React.useRef(null);

	const contextMenu = useResourceContextMenuBuilder({
		resource,
		favoriteRef,
		showModal,
	});
	const canShowTroubleshootStatus =
		shouldShowTroubleshootOption(resource, workspaceConfiguration) &&
		resourceContext?.resourceLatestStatus?.[resource.id]?.status === LaunchStatus.Failed;

	// We need to use the Favorite component's reference here
	// since GenericTile uses 3rd party Dropdown component for
	// showing dropdown menu. Favorite Component can't be used in
	// 3rd party Dropdown component. Therefore we are using refs
	// to achieve the adding/removing from Favorites functionality.
	// This is an old practice and it is only being done here because
	// custom components can't be used in Dropdown component.
	const { isLargeFormFactor, isSmallFormFactor } = useFormFactor();
	const { elementRef, inScrolledView } = useInScrolledView();

	const isOffWhiteBackground = useFeatureCanary(FeatureFlag.EnableOffWhiteBackground);
	const { isDetailedDesktopViewEnabled } = resourceContext;
	const canShowBrowserExtensionPrompt = useBrowserExtensionPrompt();

	const onClick = () =>
		launchResourceOnClick({
			workspaceConfiguration,
			resource,
			resourceContext,
			userDetails,
			canShowBrowserExtensionPrompt,
			showModal,
		});

	const shouldWrapButtonComponent = !useFeatureCanary(
		FeatureFlag.DisableResourceButtonWrap
	);

	const Wrapper = shouldWrapButtonComponent ? Button.Wrapper : DivWrapper;

	React.useEffect(() => {
		performance.markApplicationLoad(PerformanceEvents.WSUIRendered);
	}, []);

	return (
		<Wrapper
			disabled={!isResourceAvailable}
			onClick={onClick}
			style={
				isLargeFormFactor ? largeFormButtonWrapperStyle : smallFormButtonWrapperStyle
			}
		>
			<StyledDiv
				isSmallFormFactor={isSmallFormFactor}
				isResourceAvailable={isResourceAvailable}
				isOffWhiteBackground={isOffWhiteBackground}
				data-analytics-name="resource-tile"
				data-testid={resource.name}
				isDetailedDesktopViewEnabled={isDetailedDesktopViewEnabled}
				ref={elementRef}
			>
				{inScrolledView && (
					<>
						{isLargeFormFactor && hasSubscriptionsEnabled && (
							<FavoriteIconWithRef resource={resource} ref={favoriteRef} />
						)}

						<ResourceDetailsWithFavoriteRef
							resource={resource}
							ref={favoriteRef}
							isSmallFormFactor={isSmallFormFactor}
							isResourceAvailable={isResourceAvailable}
							hasSubscriptionsEnabled={hasSubscriptionsEnabled}
							canShowTroubleshootStatus={canShowTroubleshootStatus}
						/>

						<ResourceMenu resource={resource} contextMenuBuilder={() => contextMenu} />
					</>
				)}
			</StyledDiv>
		</Wrapper>
	);
}

const _GenericTile = withModals(__GenericTile);

export const GenericTile = (props: any) => {
	return (
		<GenericErrorBoundary
			renderProp={() => <_GenericTile {...props} />}
			name="GenericTile"
		/>
	);
};
